import React, { useState, useEffect, useRef } from "react";
import "../css/projectUpload.css";
import Sidebar from "../admin/Sidebar";
import Header from "../admin/Header";
import DashboardFooter from "../admin/DashboardFooter";
import { 
  FaEdit, 
  FaTrash, 
  FaDownload, 
  FaPlus, 
  FaFilePdf, 
  FaFileWord, 
  FaFileExcel, 
  FaFileImage,
  FaFileArchive,
  FaSearch, 
  FaTimes, 
  FaSync, 
  FaFilter,
  FaFolderPlus,
  FaFolderOpen,
  FaFolder
} from "react-icons/fa";
import * as XLSX from "xlsx";

const API_URL = "https://auxititpt.org/Backend/project_api.php";

const ProjectUpload = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [formData, setFormData] = useState({
    projectName: "",
    description: "",
    file: null,
    structureType: "file" // 'file' or 'folder'
  });
  const [searchQuery, setSearchQuery] = useState("");
  
  // Filter states
  const [filterFileTypes, setFilterFileTypes] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempFileTypes, setTempFileTypes] = useState([]);

  // File explorer states
  const [expandedFolders, setExpandedFolders] = useState({});
  const [currentProjectFiles, setCurrentProjectFiles] = useState(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [uploadPath, setUploadPath] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [folderName, setFolderName] = useState("");

  // used to prevent double-clicks and race conditions
  const workingRef = useRef(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error("Failed to fetch projects");
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setError("Error loading projects: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // fetch single project's latest structure and return project object
  const fetchProjectById = async (id) => {
    try {
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error("Failed to fetch projects");
      const data = await response.json();
      return data.find(p => Number(p.id) === Number(id)) || null;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  // Create or Update project
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (workingRef.current) return;
    setError("");
    if (!formData.projectName || !formData.description) {
      setError("Please fill all required fields.");
      return;
    }

    if (formData.structureType === "file" && !editProject && !formData.file) {
      setError("File is required for single file projects.");
      return;
    }

    workingRef.current = true;
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("projectName", formData.projectName);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("structureType", formData.structureType);

      if (editProject) {
        // update flow
        formDataToSend.append("action", "update_project");
        formDataToSend.append("projectId", editProject.id);
        // if file provided -> replace logic on server
        if (formData.file) {
          formDataToSend.append("file", formData.file);
        }
      } else {
        // create new
        if (formData.structureType === "file" && formData.file) {
          formDataToSend.append("file", formData.file);
        }
      }

      const response = await fetch(API_URL, {
        method: "POST",
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.status === "success") {
        await fetchProjects();
        resetForm();
        setShowFormModal(false);
      } else {
        setError(result.message || "Operation failed");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      workingRef.current = false;
    }
  };

  // Upload file inside existing project
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !currentProjectFiles) return;
    if (workingRef.current) return;

    workingRef.current = true;
    setError("");
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("projectId", currentProjectFiles.id);
      formDataToSend.append("file", selectedFile);
      // path is optional; empty means project root
      formDataToSend.append("path", uploadPath);

      const response = await fetch(API_URL, {
        method: "POST",
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.status === "success") {
        // fetch updated project and update UI
        const updated = await fetchProjectById(currentProjectFiles.id);
        if (updated) {
          setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
          setCurrentProjectFiles(updated);
        } else {
          await fetchProjects();
        }
        setSelectedFile(null);
      } else {
        setError(result.message || "File upload failed");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      workingRef.current = false;
    }
  };

  // Create folder inside existing project
  const handleFolderCreate = async (e) => {
    e.preventDefault();
    if (!folderName.trim() || !currentProjectFiles) return;
    if (workingRef.current) return;

    workingRef.current = true;
    setError("");
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("projectId", currentProjectFiles.id);
      formDataToSend.append("folderName", folderName.trim());
      formDataToSend.append("path", uploadPath);

      const response = await fetch(API_URL, {
        method: "POST",
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.status === "success") {
        const updated = await fetchProjectById(currentProjectFiles.id);
        if (updated) {
          setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
          setCurrentProjectFiles(updated);
        } else {
          await fetchProjects();
        }
        setFolderName("");
      } else {
        setError(result.message || "Folder creation failed");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      workingRef.current = false;
    }
  };

  const resetForm = () => {
    setFormData({ 
      projectName: "", 
      description: "", 
      file: null,
      structureType: "file"
    });
    setEditProject(null);
    setSelectedFile(null);
    setFolderName("");
  };

  const handleEdit = (project) => {
    setEditProject(project);
    // set structureType: if project already has structure with folders treat as folder type
    const isFolderType = project.structure && project.structure.length > 0 && project.structure.some(i => i.type === 'folder');
    setFormData({
      projectName: project.project_name,
      description: project.description,
      file: null,
      structureType: isFolderType ? "folder" : "file"
    });
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project? This will delete all files and folders associated with it.")) return;
    try {
      const response = await fetch(`${API_URL}?id=${id}`, {
        method: "DELETE"
      });
      
      const result = await response.json();
      
      if (result.status === "success") {
        await fetchProjects();
      } else {
        setError(result.message || "Delete failed");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    }
  };

  // Delete single file/folder by path
  const handleFileDelete = async (filePath) => {
    if (!window.confirm("Are you sure you want to delete this file/folder?")) return;
    if (workingRef.current) return;

    workingRef.current = true;
    setError("");
    try {
      const encodedPath = encodeURIComponent(filePath);
      const response = await fetch(`${API_URL}?path=${encodedPath}`, {
        method: "DELETE"
      });
      
      const result = await response.json();
      
      if (result.status === "success") {
        // refresh current project in modal if open, otherwise refresh list
        if (currentProjectFiles) {
          const updated = await fetchProjectById(currentProjectFiles.id);
          if (updated) {
            setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
            setCurrentProjectFiles(updated);
          } else {
            await fetchProjects();
          }
        } else {
          await fetchProjects();
        }
      } else {
        setError(result.message || "Delete failed");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      workingRef.current = false;
    }
  };

  const exportToExcel = () => {
    if (!projects || projects.length === 0) {
      alert("No project data to export");
      return;
    }
    
    const exportData = projects.map(project => ({
      'ID': project.id,
      'Project Name': project.project_name,
      'Description': project.description,
      'File Count': countFiles(project.structure),
      'Upload Date': new Date(project.uploaded_at).toLocaleString('en-GB')
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
    
    // Set column widths
    const wscols = [
      { wch: 8 },  // ID
      { wch: 30 }, // Project Name
      { wch: 50 }, // Description
      { wch: 12 }, // File Count
      { wch: 20 }  // Upload Date
    ];
    worksheet['!cols'] = wscols;
    
    XLSX.writeFile(workbook, `Project_Details_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const countFiles = (structure) => {
    if (!structure) return 0;
    let count = 0;
    structure.forEach(item => {
      if (item.type === 'file') count++;
      if (item.type === 'folder' && item.children) {
        count += countFiles(item.children);
      }
    });
    return count;
  };

  const getFileIcon = (extension) => {
    const ext = extension?.toLowerCase();
    switch (ext) {
      case 'pdf': return <FaFilePdf size={24} className="text-danger" />;
      case 'doc': 
      case 'docx': return <FaFileWord size={24} className="text-primary" />;
      case 'xls':
      case 'xlsx': return <FaFileExcel size={24} className="text-success" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <FaFileImage size={24} className="text-warning" />;
      case 'zip':
      case 'rar':
      case '7z': return <FaFileArchive size={24} className="text-info" />;
      default: return <FaFilePdf size={24} className="text-secondary" />;
    }
  };

  const toggleFolder = (path) => {
    setExpandedFolders(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const setTargetPath = (itemPath, itemType) => {
    const trimmed = itemPath.replace(/^\/+/, '').replace(/\/+$/, '');
    if (itemType === 'folder') {
      setUploadPath(trimmed);
    } else {
      const lastSlash = trimmed.lastIndexOf('/');
      const dir = lastSlash === -1 ? '' : trimmed.substring(0, lastSlash);
      setUploadPath(dir);
    }
  };

  const renderFileTree = (items, basePath = "/", projectId = null) => {
    if (!items || !Array.isArray(items)) return null;
    
    return items.map((item) => {
      const itemPath = `${basePath}${item.name}`.replace(/\/+/g, '/');
      const isExpanded = expandedFolders[itemPath];
      
      if (item.type === "folder") {
        return (
          <div key={itemPath} className="folder-item">
            <div className="folder-header d-flex justify-content-between align-items-center">
              <div onClick={() => toggleFolder(itemPath)} style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                {isExpanded ? <FaFolderOpen className="me-2 text-warning" /> : <FaFolder className="me-2 text-warning" />}
                <strong>{item.name}</strong>
              </div>
              <div>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileDelete(item.path);
                  }}
                  title="Delete folder"
                >
                  <FaTrash size={12} />
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary ms-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTargetPath(itemPath, item.type);
                  }}
                  title="Select as upload target"
                >
                  Set Upload Target
                </button>
              </div>
            </div>
            
            {isExpanded && item.children && item.children.length > 0 && (
              <div className="folder-children ms-4">
                {renderFileTree(item.children, `${itemPath}/`, projectId)}
              </div>
            )}
          </div>
        );
      }
      
      return (
        <div key={itemPath} className="file-item d-flex align-items-center py-2 border-bottom">
          <div className="file-icon me-3">
            {getFileIcon(item.extension)}
          </div>
          <div className="flex-grow-1">
            <div className="file-name fw-medium">{item.name}</div>
            <div className="file-meta text-muted small">
              {item.size ? `${(item.size / 1024).toFixed(1)} KB` : ''} • 
              {item.modified ? new Date(item.modified).toLocaleDateString() : ''}
            </div>
          </div>
          <div className="file-actions">
            <a 
              href={`https://auxititpt.org${item.path}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-primary me-2"
              title="Download file"
            >
              <FaDownload size={12} />
            </a>
            <button 
              className="btn btn-sm btn-danger"
              onClick={() => handleFileDelete(item.path)}
              title="Delete file"
            >
              <FaTrash size={12} />
            </button>
            <button
              className="btn btn-sm btn-outline-secondary ms-2"
              onClick={() => setTargetPath(itemPath, item.type)}
              title="Upload to this file's folder"
            >
              Set Upload Target
            </button>
          </div>
        </div>
      );
    });
  };

  // open file modal with fresh project structure from server
  const openFileModal = async (project) => {
    setUploadPath("");
    setExpandedFolders({});
    setShowFileModal(true);
    // get fresh structure for this project to avoid stale state
    const fresh = await fetchProjectById(project.id);
    if (fresh) setCurrentProjectFiles(fresh);
    else {
      // fall back to provided object
      setCurrentProjectFiles(project);
    }
  };

  const filteredProjects = projects.filter(project => {
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        project.project_name.toLowerCase().includes(query) || 
        project.description.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    
    // Apply file type filters
    if (filterFileTypes.length > 0) {
      const hasMatchingFile = project.structure && 
        flattenStructure(project.structure).some(file => 
          file.type === 'file' && filterFileTypes.includes((file.extension || '').toLowerCase())
        );
      if (!hasMatchingFile) return false;
    }
    
    return true;
  });

  // Helper function to flatten nested structure for filtering
  const flattenStructure = (structure) => {
    if (!structure) return [];
    let result = [];
    structure.forEach(item => {
      result.push(item);
      if (item.type === 'folder' && item.children) {
        result = [...result, ...flattenStructure(item.children)];
      }
    });
    return result;
  };

  const toggleTempFilter = (value) => {
    if (tempFileTypes.includes(value)) {
      setTempFileTypes(tempFileTypes.filter(t => t !== value));
    } else {
      setTempFileTypes([...tempFileTypes, value]);
    }
  };

  const applyFilters = () => {
    setFilterFileTypes([...tempFileTypes]);
    setShowFilterModal(false);
  };

  const clearFilters = () => {
    setFilterFileTypes([]);
    setSearchQuery("");
    setTempFileTypes([]);
  };

  if (loading) {
    return (
      <div className="d-flex dashboard-container">
        <Sidebar />
        <div className="main-content flex-grow-1">
          <Header />
          <div className="container py-5 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <DashboardFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex dashboard-container">
      <Sidebar />
      <div className="main-content flex-grow-1">
        <Header />
        
        <div className="container py-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="fw-bold">Project Management</h3>
            <div className="d-flex align-items-center">
              <button
                className="btn btn-outline-secondary me-2"
                onClick={fetchProjects}
              >
                <FaSync className="me-1" /> Refresh
              </button>
              <button 
                className="btn btn-success me-2" 
                onClick={exportToExcel}
              >
                <FaDownload className="me-2" /> Export Excel
              </button>
              <button
                className="btn btn-outline-primary me-2 d-flex align-items-center"
                onClick={() => setShowFilterModal(true)}
              >
                <FaFilter className="me-2" /> Filters
                {(filterFileTypes.length > 0) && (
                  <span className="badge bg-danger ms-2">{filterFileTypes.length}</span>
                )}
              </button>
              <button
                className="btn btn-primary d-flex align-items-center"
                onClick={() => {
                  resetForm();
                  setShowFormModal(true);
                }}
              >
                <FaPlus className="me-2" /> Add Project
              </button>
            </div>
          </div>
          
          {/* Search bar */} 
          <div className="card mb-4 p-3">
            <div className="row g-2 align-items-center">
              <div className="col-md-8">
                <div className="input-group">
                  <span className="input-group-text"><FaSearch /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search project name or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button className="btn btn-outline-secondary" onClick={() => setSearchQuery("")}>
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-4 text-end">
                <button
                  className="btn btn-outline-secondary"
                  onClick={clearFilters}
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
          
          {/* Projects Table */}
          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-dark text-center">
                <tr>
                  <th>#</th>
                  <th>Project Name</th>
                  <th>Description</th>
                  <th>Files</th>
                  <th>Upload Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project, index) => (
                  <tr key={project.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>{project.project_name}</td>
                    <td>{project.description.length > 50 ? project.description.substring(0, 50) + '...' : project.description}</td>
                    <td className="text-center">
                      {project.structure ? countFiles(project.structure) : 0}
                    </td>
                    <td>{new Date(project.uploaded_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</td>
                    <td className="text-center">
                      <button 
                        className="btn btn-sm btn-info me-1" 
                        onClick={() => openFileModal(project)}
                        title="Manage Files"
                      >
                        <FaFolderOpen />
                      </button>
                      <button 
                        className="btn btn-sm btn-warning me-1" 
                        onClick={() => handleEdit(project)}
                        title="Edit Project"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="btn btn-sm btn-danger" 
                        onClick={() => handleDelete(project.id)}
                        title="Delete Project"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredProjects.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No projects found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Project Form Modal */}
          {showFormModal && (
            <div
              className="modal show d-block"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
              onClick={() => {
                resetForm();
                setShowFormModal(false);
              }}
            >
              <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editProject ? "Edit Project" : "Add New Project"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        resetForm();
                        setShowFormModal(false);
                      }}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <form onSubmit={handleSubmit}>
                      {error && <div className="alert alert-danger">{error}</div>}
                      
                      <div className="mb-3">
                        <label className="form-label">Project Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.projectName}
                          onChange={(e) => setFormData({...formData, projectName: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label">Description *</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={formData.description}
                          onChange={(e) => setFormData({...formData, description: e.target.value})}
                          required
                        ></textarea>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label">Project Structure</label>
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="structureType"
                            id="singleFile"
                            checked={formData.structureType === "file"}
                            onChange={() => setFormData({...formData, structureType: "file"})}
                          />
                          <label className="form-check-label" htmlFor="singleFile">
                            Single file upload
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="structureType"
                            id="folderStructure"
                            checked={formData.structureType === "folder"}
                            onChange={() => setFormData({...formData, structureType: "folder"})}
                          />
                          <label className="form-check-label" htmlFor="folderStructure">
                            Folder structure (create folders and upload multiple files)
                          </label>
                        </div>
                      </div>
                      
                      {formData.structureType === "file" && (
                        <div className="mb-3">
                          <label className="form-label">
                            {editProject ? "Replace Project File (optional)" : "Project File *"}
                          </label>
                          <input
                            type="file"
                            className="form-control"
                            onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif,.txt,.json"
                          />
                          <small className="text-muted d-block mt-1">
                            Allowed formats: PDF, DOC/DOCX, XLS/XLSX, PPT/PPTX, ZIP/RAR, JPG/JPEG, PNG, GIF, TXT, JSON (Max 100MB)
                          </small>
                          
                          {editProject && editProject.structure && editProject.structure.length > 0 && (
                            <div className="mt-2">
                              <small>Current files:</small>
                              <ul>
                                {editProject.structure.map((it, i) => (
                                  <li key={i} className="small">{it.name} ({it.type})</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            resetForm();
                            setShowFormModal(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-primary">
                          {editProject ? "Update Project" : "Create Project"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* File Management Modal */}
          {showFileModal && currentProjectFiles && (
            <div
              className="modal show d-block"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
              onClick={() => setShowFileModal(false)}
            >
              <div className="modal-dialog modal-xl" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      Manage Files: {currentProjectFiles.project_name}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowFileModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="file-browser">
                          <div className="current-path mb-3 p-2 bg-light rounded d-flex align-items-center">
                            <span className="me-2">Current path: /</span>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={uploadPath} 
                              onChange={(e) => setUploadPath(e.target.value.replace(/^\//, '').replace(/\/$/, ''))} 
                              style={{flex: 1}} 
                            />
                          </div>
                          
                          <div className="border rounded p-3 mb-3" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {currentProjectFiles.structure && currentProjectFiles.structure.length > 0 ? (
                              <div className="file-tree">
                                {renderFileTree(currentProjectFiles.structure, "/", currentProjectFiles.id)}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-muted">
                                <FaFolder size={48} className="mb-2" />
                                <p>No files or folders yet. Upload your first file or create a folder.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <div className="upload-section border rounded p-3 h-100">
                          <h6 className="border-bottom pb-2 mb-3">Upload New File</h6>
                          <form onSubmit={handleFileUpload}>
                            <div className="mb-3">
                              <label className="form-label">Select File</label>
                              <input
                                type="file"
                                className="form-control"
                                onChange={(e) => setSelectedFile(e.target.files[0])}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif,.txt,.json"
                              />
                            </div>
                            <div className="mb-3">
                              <small className="text-muted">Upload target: /{uploadPath}</small>
                            </div>
                            <button 
                              type="submit" 
                              className="btn btn-primary w-100"
                              disabled={!selectedFile}
                            >
                              Upload File
                            </button>
                          </form>
                          
                          <div className="my-4">
                            <h6 className="border-bottom pb-2 mb-3">Create New Folder</h6>
                            <form onSubmit={handleFolderCreate}>
                              <div className="mb-3">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Folder name"
                                  value={folderName}
                                  onChange={(e) => setFolderName(e.target.value)}
                                  maxLength="50"
                                />
                              </div>
                              <div className="mb-2">
                                <small className="text-muted">Create inside: /{uploadPath}</small>
                              </div>
                              <button 
                                type="submit" 
                                className="btn btn-warning w-100"
                                disabled={!folderName.trim()}
                              >
                                <FaFolderPlus className="me-2" />
                                Create Folder
                              </button>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowFileModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Filter Modal */}
          {showFilterModal && (
            <div
              className="modal show d-block"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
              onClick={() => setShowFilterModal(false)}
            >
              <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Filter Projects</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowFilterModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-4">
                      <h6>File Types</h6>
                      <div className="filter-list" style={{ maxHeight: 200, overflowY: "auto" }}>
                        {['pdf', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar', 'jpg', 'jpeg', 'png', 'txt', 'json'].map((type) => (
                          <div key={type} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`filetype_${type}`}
                              checked={tempFileTypes.includes(type)}
                              onChange={() => toggleTempFilter(type)}
                            />
                            <label className="form-check-label" htmlFor={`filetype_${type}`}>
                              {type.toUpperCase()}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary me-2"
                      onClick={() => setTempFileTypes([])}
                    >
                      Clear Selections
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() => setShowFilterModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={applyFilters}
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <DashboardFooter />
      </div>
    </div>
  );
};

export default ProjectUpload;